import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, Link2, Loader2, Plus, Upload } from 'lucide-react';
import {
    useAdminAddVideoLinkMutation,
    useAdminGetCourseQuery,
    useAdminUpdateCourseMutation,
    useAdminUploadVideoMutation,
} from '../../store/api/apiSlice';
import type { AddVideoLinkRequest } from '../../types';
import { validateVideoFile } from '../../utils/fileValidation';
import { readableError } from './lib';
import VideoAdminCard from './components/admin/VideoAdminCard';

export default function AdminCourse() {
    const { courseId = '' } = useParams();
    const navigate = useNavigate();
    const { data: course, isLoading, isError, error } = useAdminGetCourseQuery(courseId, { skip: !courseId });
    const [updateCourse, { isLoading: isSavingCourse }] = useAdminUpdateCourseMutation();
    const [addVideoLink, { isLoading: isAddingLink }] = useAdminAddVideoLinkMutation();
    const [uploadVideo, { isLoading: isUploading }] = useAdminUploadVideoMutation();

    const [passingScore, setPassingScore] = useState<number | null>(null);
    const [mode, setMode] = useState<'link' | 'upload'>('link');
    const [linkForm, setLinkForm] = useState({ title: '', videoUrl: '', durationSeconds: '' });
    const [uploadForm, setUploadForm] = useState<{ title: string; file: File | null }>({ title: '', file: null });
    const [addError, setAddError] = useState('');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
            </div>
        );
    }

    if (isError || !course) {
        return (
            <div className="p-4 md:p-8">
                <BackLink onClick={() => navigate('/training/admin')} />
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                    {readableError(error, 'Could not load this course.')}
                </div>
            </div>
        );
    }

    const videos = [...(course.videos ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
    const scoreValue = passingScore ?? course.passingScore;

    const handleAddLink = async () => {
        setAddError('');
        if (!linkForm.title.trim() || !linkForm.videoUrl.trim()) {
            setAddError('Title and video URL are required.');
            return;
        }
        const body: AddVideoLinkRequest = { title: linkForm.title.trim(), videoUrl: linkForm.videoUrl.trim() };
        if (linkForm.durationSeconds) body.durationSeconds = Number(linkForm.durationSeconds);
        try {
            await addVideoLink({ courseId, body }).unwrap();
            setLinkForm({ title: '', videoUrl: '', durationSeconds: '' });
        } catch (err) {
            setAddError(readableError(err, 'Could not add the lesson.'));
        }
    };

    const handleUpload = async () => {
        setAddError('');
        if (!uploadForm.title.trim() || !uploadForm.file) {
            setAddError('Title and a video file are required.');
            return;
        }
        const fd = new FormData();
        fd.append('file', uploadForm.file);
        fd.append('title', uploadForm.title.trim());
        try {
            await uploadVideo({ courseId, formData: fd }).unwrap();
            setUploadForm({ title: '', file: null });
        } catch (err) {
            setAddError(readableError(err, 'Could not upload the lesson.'));
        }
    };

    return (
        <div className="p-4 md:p-8">
            <BackLink onClick={() => navigate('/training/admin')} />

            {/* Course settings */}
            <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
                            {course.isPublished ? (
                                <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                                    Published
                                </span>
                            ) : (
                                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                                    Draft
                                </span>
                            )}
                        </div>
                        {course.description && <p className="mt-1 text-sm text-gray-500">{course.description}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={() => updateCourse({ id: courseId, body: { isPublished: !course.isPublished } })}
                        className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        {course.isPublished ? 'Unpublish' : 'Publish course'}
                    </button>
                </div>

                <div className="mt-4 flex flex-wrap items-end gap-3">
                    <div>
                        <label className="mb-1.5 block text-xs font-medium text-gray-600">Passing score (%)</label>
                        <input
                            type="number"
                            min={1}
                            max={100}
                            value={scoreValue}
                            onChange={(e) => setPassingScore(Number(e.target.value) || 0)}
                            className="w-32 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                        />
                    </div>
                    <button
                        type="button"
                        disabled={isSavingCourse || scoreValue === course.passingScore}
                        onClick={() => updateCourse({ id: courseId, body: { passingScore: scoreValue } })}
                        className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                        {isSavingCourse && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save
                    </button>
                </div>
            </div>

            {/* Add lesson */}
            <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 md:p-6">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Add a lesson</h2>
                <div className="mb-4 inline-flex rounded-full bg-gray-100 p-1">
                    <button
                        type="button"
                        onClick={() => setMode('link')}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium ${
                            mode === 'link' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                        }`}
                    >
                        <Link2 className="h-4 w-4" /> Link
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('upload')}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium ${
                            mode === 'upload' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                        }`}
                    >
                        <Upload className="h-4 w-4" /> Upload
                    </button>
                </div>

                {mode === 'link' ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <input
                            value={linkForm.title}
                            onChange={(e) => setLinkForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="Lesson title"
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                        />
                        <input
                            value={linkForm.videoUrl}
                            onChange={(e) => setLinkForm((f) => ({ ...f, videoUrl: e.target.value }))}
                            placeholder="https://youtu.be/…"
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                        />
                        <input
                            type="number"
                            min={0}
                            value={linkForm.durationSeconds}
                            onChange={(e) => setLinkForm((f) => ({ ...f, durationSeconds: e.target.value }))}
                            placeholder="Duration (seconds, optional)"
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                        />
                        <button
                            type="button"
                            onClick={handleAddLink}
                            disabled={isAddingLink}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#ff5722] disabled:opacity-50 md:w-fit"
                        >
                            {isAddingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Add lesson
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <input
                            value={uploadForm.title}
                            onChange={(e) => setUploadForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="Lesson title"
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                        />
                        <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0] ?? null;
                                if (file) {
                                    const validationError = validateVideoFile(file);
                                    if (validationError) {
                                        setAddError(validationError);
                                        e.target.value = '';
                                        setUploadForm((f) => ({ ...f, file: null }));
                                        return;
                                    }
                                }
                                setAddError('');
                                setUploadForm((f) => ({ ...f, file }));
                            }}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm file:font-medium"
                        />
                        <p className="text-xs text-gray-400 md:col-span-2">
                            Max 200&nbsp;MB. For larger videos, host externally and add as a link.
                        </p>
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={isUploading}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#ff5722] disabled:opacity-50 md:w-fit"
                        >
                            {isUploading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4" />
                            )}
                            Upload lesson
                        </button>
                    </div>
                )}
                {addError && <p className="mt-3 text-sm text-red-600">{addError}</p>}
            </div>

            {/* Lessons */}
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Lessons ({videos.length})</h2>
            {videos.length === 0 ? (
                <div className="rounded-2xl border border-gray-100 bg-white p-10 text-center text-sm text-gray-500">
                    No lessons yet. Add one above.
                </div>
            ) : (
                <div className="space-y-3">
                    {videos.map((v, i) => (
                        <VideoAdminCard key={v.id} video={v} courseId={courseId} index={i} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BackLink({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
            <ChevronLeft className="h-4 w-4" /> Back to courses
        </button>
    );
}
